import React, {useState, useEffect, useMemo} from 'react'
import {formatCreditAmount} from '../../Utils/_helpers/number'
import {PriceRate} from '../../types'

export interface CreditOption {
  amount: number
  price: {
    sms: string
    mms: string
  }
  discountPercentage: {
    sms: string
    mms: string
  }
}

interface MinimumCredit {
  sms: number
  mms: number
}

interface PriceRateList {
  sms: PriceRate[]
  mms: PriceRate[]
}

interface CreditSelectionProps {
  quickSms: 'sms' | 'mms'
  minCredit: MinimumCredit
  priceRateList: PriceRateList
  error: string
  noOfCredit: number
  discount: number
  maxCredit: number
  leastPrice: number
  handleChange: (value: string) => void
  handleError: (error: string) => void
  handleSetDiscount: (discount: number) => void
  purchaseCreditInputRef?: React.RefObject<HTMLInputElement>
}

const CreditSelection: React.FC<CreditSelectionProps> = ({
  quickSms = 'sms',
  minCredit = {sms: 0, mms: 0},
  priceRateList = {sms: [], mms: []},
  error = '',
  noOfCredit = 0,
  discount = 0,
  maxCredit = 200000,
  leastPrice = 0.047,
  handleChange,
  handleError,
  handleSetDiscount,
  purchaseCreditInputRef,
}) => {
  const [selectedOption, setSelectedOption] = useState<number>(0)

  const creditOptions = useMemo<CreditOption[]>(() => {
    const {sms: smsRates, mms: mmsRates} = priceRateList

    const calculateDiscount = (actualPrice: number, finalPrice: number) => {
      return actualPrice > finalPrice ? `${((1 - finalPrice / actualPrice) * 100).toFixed(0)}%` : ''
    }

    const minCredits = minCredit[quickSms]

    const findPrice = (amount: number, rates: PriceRate[]) => {
      const rate = rates.find((r) => amount >= r.creditstartrange && amount <= r.creditendrange)
      return rate ? rate.finalPrice : 0
    }

    // Find the price range for the minCredit
    const smsRateForMinCredit = smsRates.find(
      (r) => minCredits >= r.creditstartrange && minCredits <= r.creditendrange
    )
    const mmsRateForMinCredit = mmsRates.find(
      (r) => minCredits >= r.creditstartrange && minCredits <= r.creditendrange
    )

    if (!smsRateForMinCredit || !mmsRateForMinCredit) {
      return []
    }

    // Create a CreditOption for the minCredit amount
    const minCreditOption: CreditOption = {
      amount: minCredits,
      price: {
        sms: `$${findPrice(minCredits, smsRates).toFixed(3)}`,
        mms: `$${findPrice(minCredits, mmsRates).toFixed(2)}`,
      },
      discountPercentage: {
        sms: calculateDiscount(leastPrice, smsRateForMinCredit.finalPrice),
        mms: calculateDiscount(leastPrice, mmsRateForMinCredit.finalPrice),
      },
    }

    // Find all SMS and MMS rates that include or are above minCredits
    const validSmsRates = smsRates.filter((rate) => rate.creditendrange >= minCredits)
    const validMmsRates = mmsRates.filter((rate) => rate.creditendrange >= minCredits)

    // Sort the valid rates by creditstartrange
    const sortedSmsRates = validSmsRates.sort((a, b) => a.creditstartrange - b.creditstartrange)
    const sortedMmsRates = validMmsRates.sort((a, b) => a.creditstartrange - b.creditstartrange)

    // Generate credit options starting from minCredit
    const optionsFromRates = sortedSmsRates.map((smsOption) => {
      const matchingMMS = sortedMmsRates.find(
        (mmsOption) => mmsOption.creditstartrange === smsOption.creditstartrange
      )

      const smsDiscount = calculateDiscount(leastPrice, smsOption.finalPrice)
      const mmsDiscount = matchingMMS ? calculateDiscount(leastPrice, matchingMMS.finalPrice) : ''

      return {
        amount: smsOption.creditstartrange,
        price: {
          sms: `$${smsOption.finalPrice.toFixed(3)}`,
          mms: matchingMMS ? `$${matchingMMS.finalPrice.toFixed(2)}` : '$0.000',
        },
        discountPercentage: {
          sms: smsDiscount,
          mms: mmsDiscount,
        },
      }
    })

    // Combine the minCredit option with the options from rates
    return [minCreditOption, ...optionsFromRates]
  }, [priceRateList.sms, priceRateList.mms, minCredit, quickSms, leastPrice, priceRateList])

  const handleResetDiscount = () => {
    if (discount !== 0) handleSetDiscount(0)
  }

  const handleCreditOptionClick = (amount: number) => {
    handleError(amount.toString())
    handleResetDiscount()
    setSelectedOption(amount)
    handleChange(amount.toString())
  }

  useEffect(() => {
    if (noOfCredit === 0 || error) {
      setSelectedOption(0)
    }
  }, [noOfCredit])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/,/g, '')
    const numericVal = Number(val)

    handleError(val)
    handleResetDiscount()
    handleChange(val)

    const matchedOption = creditOptions.find((option, index) => {
      const nextOption = creditOptions[index + 1]
      return (
        numericVal >= option.amount &&
        (!nextOption || numericVal < nextOption.amount || numericVal < 250)
      )
    })

    setSelectedOption(matchedOption?.amount || 0)
  }

  // You can adjust the value to show more or fewer options here
  const filteredCreditOptions = creditOptions.filter(
    (option) =>
      option.amount >= minCredit[quickSms] &&
      option.amount <= maxCredit &&
      option.amount !== 112 &&
      option.amount !== 3000
  )

  return (
    <div style={{marginTop: '10px'}}>
      <h5 className='mb-4 text-start'>Select how many credits you want or type the amount</h5>
      <div
        className='d-grid mb-4'
        style={{gridTemplateColumns: 'repeat(3, 1fr)', marginRight: '180px', gap: '14px'}}
      >
        {filteredCreditOptions.map((option) => {
          const discount = option.discountPercentage[quickSms]
          return (
            <button
              key={option.amount}
              className={`btn btn-outline btn-outline-dashed btn-outline-default position-relative ${
                selectedOption === option.amount && 'active'
              }`}
              style={{borderRadius: '8px', paddingTop: '10px', paddingBottom: '10px'}}
              onClick={() => handleCreditOptionClick(option.amount)}
            >
              {discount && option.amount === 100000 && (
                <span
                  className='fw-boldest position-absolute top-0 translate-middle badge bg-primary text-white rounded-3'
                  style={{left: '76%'}}
                >
                  Save {discount}
                </span>
              )}
              <label
                style={{
                  fontWeight: 700,
                  fontSize: '14px',
                }}
              >
                {formatCreditAmount(option.amount)}
              </label>
              <p
                style={{
                  fontWeight: 500,
                  fontSize: '10px',
                  marginBottom: '0px',
                }}
              >
                1 {quickSms.toUpperCase()} = {option.price[quickSms]}
              </p>
            </button>
          )
        })}
      </div>
      <div className='tab-content' id='myTabContent' style={{marginTop: '24px'}}>
        <div
          className='tab-pane fade active show'
          id='Now'
          role='tabpanel'
          aria-labelledby='Now-tab'
        >
          <div className='d-flex flex-column'>
            <h5>Your Credit</h5>
            <div className='credit-box'>
              <div className='box-inner'>
                <input
                  ref={purchaseCreditInputRef}
                  maxLength={maxCredit.toString().length + 1}
                  type='text'
                  value={new Intl.NumberFormat('en-US').format(noOfCredit)}
                  onChange={handleInputChange}
                />
                {error && <span className='text-danger'>{error}</span>}
                <p>
                  1 credit = 1 Message ({quickSms === 'sms' ? 165 : 1000} characters). Credit never
                  expire
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CreditSelection
